import React, { useContext, useEffect, useState } from "react";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import INotification from "../../../../models/INotification";
import IOnGetOpenOrdersReportDTO from "../../../../models/DTO/IOnGetOpenOrdersReportDTO";
import SupplierContext from "../../../../contexts/SupplierContext";
import UserContext from "../../../../contexts/UserContext";
import IGetOpenOrdersReportDTO from "../../../../models/DTO/IGetOpenOrdersReportDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { NumericFormat } from "react-number-format";
import OwTable from "../../../shared/OwTable/OwTable";
import IOwTableCols from "../../../../models/IOwTableCols";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import IOrder from "../../../../models/IOrder";
import * as _ from "lodash-es";
import UserService from "../../../../services/UserService";
import ExcelJS from "exceljs/dist/exceljs.min.js";
import moment from "moment-timezone";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import { Socket } from "socket.io-client";
import { Link } from "react-router-dom";

type Props = {
    setActivityIndicator: (activityIndicator: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    processedReport: IProcessed_Report;
};

export const OpenOrdersReport: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const renderSubTotal = (row: Partial<IOrder>) => {
        const calculatedSubTotal = Number(row.sub_total || 0) - Number(row.discount_total || 0);

        return (
            <NumericFormat
                value={calculatedSubTotal}
                displayType={"text"}
                decimalScale={supplierModel.pricing_precision || 2}
                fixedDecimalScale={true}
                thousandSeparator={true}
            />
        );
    };

    const renderOrderName = (row: Partial<IOrder>) => {
        return (
            <div>
                <Link to={`/orders/${row.guid}/entry`}>{row.name}</Link>
            </div>
        );
    };

    const renderOrderStatus = (row: Partial<IOrder>) => {
        return _.startCase((row.Order_State?.name || "").toLowerCase());
    };

    const renderLastUpdated = (row: Partial<IOrder>) => {
        return UserService.parseLastUpdated(row.updated_at);
    };

    const getTableCols = (): IOwTableCols[] => {
        return [
            {
                title: "order",
                valueProp: "name",
                renderFn: renderOrderName,
                renderFnEmitsRow: true,
            },
            {
                title: "account",
                valueProp: "Vendor",
                valueSubProp: "name",
            },
            {
                title: "description",
                valueProp: "description",
            },
            {
                title: "status",
                valueProp: "status",
                renderFn: renderOrderStatus,
                renderFnEmitsRow: true,
            },
            {
                title: "created_at",
                valueProp: "created_at",
                renderFn: renderLastUpdated,
                renderFnEmitsRow: true,
            },
            {
                title: "last_updated",
                valueProp: "updated_at",
                renderFn: renderLastUpdated,
                renderFnEmitsRow: true,
            },
            {
                title: "amount",
                valueProp: "sub_total",
                renderFn: renderSubTotal,
                renderFnEmitsRow: true,
                className: "text-end",
            },
        ];
    };

    const [reportData, setReportData] = useState<IOnGetOpenOrdersReportDTO | undefined>(props.processedReport.report_data);
    const [tableCols, setTableCols] = useState<IOwTableCols[]>(getTableCols());
    const [dataFetch, setDataFetch] = useState(false);

    const downloadExcel = () => {
        if (reportData) {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet("Open Orders");

            ws.columns = [
                { header: "Order", key: "order_name" },
                { header: "Account", key: "account" },
                { header: "Description", key: "description" },
                { header: "Status", key: "status" },
                { header: "Created At", key: "created_at" },
                { header: "Last Updated", key: "last_updated" },
                { header: "Amount", key: "amount" },
            ];

            const headerCells = ["A1", "B1", "C1", "D1", "E1"];

            for (const header of headerCells) {
                ws.getCell(header).font = {
                    bold: true,
                };
            }

            for (const order of reportData.orders) {
                const calculatedSubTotal = Number(order.sub_total || 0) - Number(order.discount_total || 0);

                ws.addRow({
                    order_name: order.name,
                    account: order.Vendor?.name,
                    status: _.startCase((order.Order_State?.name || "").toLowerCase()),
                    last_updated: UserService.parseLastUpdated(order.updated_at),
                    amount: calculatedSubTotal || 0,
                    description: order.description,
                    created_at: UserService.parseLastUpdated(order.created_at),
                });
            }

            const totalRows = reportData.orders?.length || 0;

            ws.getColumn(5).numFmt = "0.00";

            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `Orderwerks Open Orders Report.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    return (
        <div className={"OpenOrdersReport"}>
            <OwTitle title={"open_orders_report"} tag={"h3"} />
            {reportData ? <OwLabel title={"excel"} fn={downloadExcel} leftIconClass={"fas fa-download"} /> : null}
            <div className={"row"}>
                <div className="col text-start">
                    <OwLabel title={"total_orders"} className={"me-2"} />
                    {(reportData?.orders || []).length}
                </div>
                <div className="col text-end">
                    <OwLabel title={"total_amount"} className={"me-2"} />
                    <NumericFormat
                        value={reportData?.grandTotal}
                        thousandSeparator={true}
                        fixedDecimalScale={true}
                        decimalScale={supplierModel.pricing_precision || 2}
                        displayType={"text"}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col">
                    <OwTable
                        columns={tableCols}
                        dataSet={reportData?.orders || []}
                        userModel={userModel}
                        supplierModel={supplierModel}
                        dataFetch={dataFetch}
                        initialData={dataFetch}
                    />
                </div>
            </div>
        </div>
    );
};
